import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAdminApi, errMsg, useSite } from "@/lib/site";
import { AdminTitle } from "./AdminMessages";
import { Field, inputCls } from "./AdminPackages";

const BLANK = { title: "New case study", client: "", category: "", summary: "", metrics: [], image: "", sample: false, order: 99 };

const CaseEditor = ({ c, onSaved, onDeleted }) => {
  const call = useAdminApi();
  const [f, setF] = useState({ ...c, metricsText: c.metrics.join("\n") });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { ...f, order: Number(f.order), metrics: f.metricsText.split("\n").map((s) => s.trim()).filter(Boolean) };
    delete body.metricsText; delete body.id;
    try { onSaved(await call("put", `/admin/cases/${c.id}`, body)); toast.success("Case study saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    try { await call("delete", `/admin/cases/${c.id}`); onDeleted(c.id); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-3xl bg-white border p-5 sm:p-6 space-y-3" data-testid={`admin-case-${c.id}`}>
      <div className="flex justify-between items-center gap-3">
        <p className="font-display font-bold">{c.title}</p>
        <label className="flex items-center gap-2 text-sm">Sample label<Switch checked={f.sample} onCheckedChange={(v) => setF({ ...f, sample: v })} data-testid={`admin-case-sample-${c.id}`} /></label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Title"><input className={inputCls} value={f.title} onChange={set("title")} data-testid={`admin-case-title-${c.id}`} /></Field>
        <Field label="Client"><input className={inputCls} value={f.client} onChange={set("client")} data-testid={`admin-case-client-${c.id}`} /></Field>
        <Field label="Category"><input className={inputCls} value={f.category} onChange={set("category")} data-testid={`admin-case-category-${c.id}`} /></Field>
        <Field label="Order"><input type="number" className={inputCls} value={f.order} onChange={set("order")} data-testid={`admin-case-order-${c.id}`} /></Field>
      </div>
      <Field label="Summary"><textarea rows={2} className={inputCls} value={f.summary} onChange={set("summary")} data-testid={`admin-case-summary-${c.id}`} /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Results (one per line)"><textarea rows={3} className={inputCls} value={f.metricsText} onChange={set("metricsText")} data-testid={`admin-case-metrics-${c.id}`} /></Field>
        <Field label="Image URL"><input className={inputCls} value={f.image} onChange={set("image")} data-testid={`admin-case-image-${c.id}`} /></Field>
      </div>
      <div className="flex justify-between">
        <button onClick={del} className="flex items-center gap-1.5 text-sm text-red-600 hover:underline" data-testid={`admin-case-delete-${c.id}`}><Trash2 className="w-4 h-4" /> Delete</button>
        <button onClick={save} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 text-sm font-semibold" data-testid={`admin-case-save-${c.id}`}>Save</button>
      </div>
    </div>
  );
};

export default function AdminCases() {
  const call = useAdminApi();
  const { cases, reload } = useSite();
  const [list, setList] = useState(cases);
  useEffect(() => {
    setList(cases);
  }, [cases]);
  const add = async () => {
    try { await call("post", "/admin/cases", BLANK); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-cases">
      <div className="flex items-start justify-between gap-3"><AdminTitle sub="Case studies on the Work page">Work</AdminTitle>
        <button onClick={add} className="flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-semibold" data-testid="admin-case-add-btn"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="space-y-4">
        {list.map((c) => <CaseEditor key={c.id} c={c} onSaved={() => reload()} onDeleted={() => reload()} />)}
      </div>
    </div>
  );
}
